import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("talmia_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Auth refresh failed:", err);
      localStorage.removeItem("talmia_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-check access quietly on tab focus + every 5 min so admin changes land without a reload.
  useEffect(() => {
    const recheck = async () => {
      if (!localStorage.getItem("talmia_token") || document.visibilityState === "hidden") return;
      try { const { data } = await api.get("/auth/me"); setUser((prev) => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data)); }
      catch (err) { if (err?.response?.status === 401) { localStorage.removeItem("talmia_token"); setUser(null); } }
    };
    const iv = setInterval(recheck, 5 * 60 * 1000);
    window.addEventListener("focus", recheck);
    return () => { clearInterval(iv); window.removeEventListener("focus", recheck); };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("talmia_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("talmia_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/squad/status", { status: "offline" }); } catch { /* best-effort sign-off */ }
    try { await api.post("/auth/logout"); } catch (err) { if (process.env.NODE_ENV !== "production") console.error("Logout request failed:", err); }
    localStorage.removeItem("talmia_token");
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
